import { useEffect, useMemo, useState } from "react";
import { BarChart3, Wallet } from "lucide-react";
import { getReleaseRoyalties, getReleaseMetrics } from "@/lib/afmApi";

// Regalías y métricas REALES de la distribuidora, por mes.
// Reglas de la distribuidora: sin datos -> "Aún no hay reportes" (nunca $0 ni 0 oyentes);
// no sumar monedas distintas; null = dato no entregado (se muestra "—").
const months = () => {
  const out = [];
  const d = new Date();
  for (let i = 1; i <= 12; i++) {
    const x = new Date(d.getFullYear(), d.getMonth() - i, 1);
    out.push(`${x.getFullYear()}-${String(x.getMonth() + 1).padStart(2, "0")}`);
  }
  return out;
};
const monthLabel = (p) => { const [y, m] = p.split("-"); return new Date(+y, +m - 1, 1).toLocaleDateString("es-VE", { month: "long", year: "numeric" }); };
const num = (v) => (v === null || v === undefined || v === "" ? "—" : Number(v).toLocaleString("es-VE"));
const money = (v, cur) => (v === null || v === undefined || v === "" ? "—" : `${Number(v).toLocaleString("es-VE", { minimumFractionDigits: 2, maximumFractionDigits: 4 })} ${cur || ""}`.trim());

function groupSum(rows, key, field) {
  const m = new Map();
  rows.forEach((r) => {
    const k = r[key] || "—";
    const v = r[field];
    if (v === null || v === undefined) { if (!m.has(k)) m.set(k, null); return; }
    m.set(k, (m.get(k) || 0) + Number(v));
  });
  return [...m.entries()].sort((a, b) => (b[1] || 0) - (a[1] || 0));
}

export default function ReleaseReports({ releaseId, isAdmin = false, show = ["royalties", "metrics"] }) {
  const opts = useMemo(months, []);
  const [period, setPeriod] = useState(opts[0]);
  const [tab, setTab] = useState(show.includes("royalties") ? "royalties" : "metrics");
  const [res, setRes] = useState(undefined);
  const [hidden, setHidden] = useState(false);

  useEffect(() => {
    setRes(undefined);
    const fn = tab === "royalties" ? getReleaseRoyalties : getReleaseMetrics;
    fn(releaseId, period).then(setRes).catch((e) => {
      if ([403, 404, 503].includes(e?.response?.status)) setHidden(true);
      else setRes({ error: e?.response?.data?.detail || "No se pudo consultar la distribuidora" });
    });
  }, [releaseId, period, tab]);

  if (hidden) return null;
  const rows = res?.data || [];
  const byCurrency = {};
  rows.forEach((r) => { (byCurrency[r.currency || "—"] = byCurrency[r.currency || "—"] || []).push(r); });

  return (
    <div className="rounded-xl border border-slate-200 bg-white p-6" data-testid="release-reports">
      <div className="mb-4 flex flex-wrap items-center gap-3">
        <div className="flex rounded-lg border border-slate-200 p-0.5">
          {show.includes("royalties") && <button onClick={() => setTab("royalties")} className={`flex items-center gap-1.5 rounded-md px-3 py-1.5 text-xs font-semibold ${tab === "royalties" ? "bg-black text-white" : "text-slate-600"}`}><Wallet className="h-3.5 w-3.5" /> Regalías</button>}
          {show.includes("metrics") && <button onClick={() => setTab("metrics")} className={`flex items-center gap-1.5 rounded-md px-3 py-1.5 text-xs font-semibold ${tab === "metrics" ? "bg-black text-white" : "text-slate-600"}`}><BarChart3 className="h-3.5 w-3.5" /> Métricas</button>}
        </div>
        <select value={period} onChange={(e) => setPeriod(e.target.value)} className="rounded-lg border border-slate-300 bg-white px-3 py-1.5 text-sm capitalize">
          {opts.map((p) => <option key={p} value={p}>{monthLabel(p)}</option>)}
        </select>
        <span className="ml-auto text-[11px] text-slate-400">Datos reales de la distribuidora</span>
      </div>

      {res === undefined && <p className="text-sm text-slate-400">Consultando…</p>}
      {res?.error && <p className="text-sm text-red-600">{res.error}</p>}
      {res && !res.error && !rows.length && (
        <p className="rounded-lg border border-dashed border-slate-300 py-8 text-center text-sm text-slate-500">Aún no hay reportes para este período.</p>
      )}

      {tab === "royalties" && rows.length > 0 && Object.entries(byCurrency).map(([cur, list]) => (
        <div key={cur} className="mb-5">
          <div className="mb-2 text-xs font-semibold uppercase tracking-wider text-slate-500">Moneda: {cur}</div>
          <table className="w-full text-sm">
            <thead><tr className="text-left text-[11px] uppercase tracking-wider text-slate-400"><th className="py-1">Tienda</th><th>País</th><th className="text-right">Cantidad</th>{isAdmin && <th className="text-right">Bruto</th>}{isAdmin && <th className="text-right">Neto proveedor</th>}<th className="text-right">{isAdmin ? "A pagar (partner)" : "Neto reportado"}</th></tr></thead>
            <tbody>
              {list.map((r, i) => (
                <tr key={r.source_reference || i} className="border-t border-slate-100">
                  <td className="py-1.5">{r.store || "—"}</td><td>{r.country || "—"}</td>
                  <td className="text-right font-mono">{num(r.quantity)}</td>
                  {isAdmin && <td className="text-right font-mono">{money(r.gross_amount, cur)}</td>}
                  {isAdmin && <td className="text-right font-mono">{money(r.provider_net_amount, cur)}</td>}
                  <td className="text-right font-mono font-semibold">{money(r.partner_payable_amount, cur)}</td>
                </tr>
              ))}
            </tbody>
          </table>
          {!isAdmin && <p className="mt-2 text-[11px] text-slate-400">Tu parte según tu acuerdo se refleja en Contabilidad & Regalías.</p>}
        </div>
      ))}

      {tab === "metrics" && rows.length > 0 && (
        <div className="grid gap-5 sm:grid-cols-2">
          {[["store", "Por tienda"], ["country", "Por país"], ["city", "Por ciudad"], ["age_band", "Por edad"]].map(([k, label]) => {
            const streams = groupSum(rows.filter((r) => r.metric === "streams"), k, "value");
            const listeners = groupSum(rows.filter((r) => r.metric === "listeners"), k, "value");
            if (!streams.length && !listeners.length) return null;
            const keys = [...new Set([...streams.map((x) => x[0]), ...listeners.map((x) => x[0])])].slice(0, 10);
            const sMap = new Map(streams), lMap = new Map(listeners);
            return (
              <div key={k}>
                <div className="mb-1 text-xs font-semibold uppercase tracking-wider text-slate-500">{label}</div>
                <table className="w-full text-sm">
                  <thead><tr className="text-left text-[11px] text-slate-400"><th></th><th className="text-right">Streams</th><th className="text-right">Oyentes</th></tr></thead>
                  <tbody>{keys.map((key) => <tr key={key} className="border-t border-slate-100"><td className="py-1">{key}</td><td className="text-right font-mono">{num(sMap.get(key))}</td><td className="text-right font-mono">{num(lMap.get(key))}</td></tr>)}</tbody>
                </table>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}
